import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import toast from 'react-hot-toast';
import axios from 'axios';
import {
    useDispatch,
    useSelector
} from 'react-redux';

import Yup from '../../../../../utils/yupExtensions';
import styles from './UploadResume.module.css';
import Button from '../../../Buttons/Button/Button';
import BasicPopup from '../../../Popups/BasicPopup/BasicPopup';
import BubbleButton from '../../../Buttons/BubbleButton/BubbleButton';
import CVAndCoverLetter from '../../../../Forms/Common/CVAndCoverLetter/CVAndCoverLetter';
import FormRichTextField from '../../../FormUI/FormRichTextField/FormRichTextField';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { sendJobProposal } from '../../../../../services/jobService';
import { updateApplications } from '../../../../../app/features/userSlice';
import GlobalModal from '../../../Popups/GlobalModal';
import {
    EyeBlueIcon,
    PlusIcon
} from '../../../../../assets/images/icons';

function UploadResume({ job, selectedResume, cvPair, setCVPair, agreeTermsAndCondition, setAgreeTermsAndCondition, setSelectedResume ,isSuccessPopupOpen, setIsSuccessPopupOpen,hasSkillMatch}) {
    const { t } = useTranslation();
    const [cvPairs, setCvPairs] = useState([]);
    const [formLoading, setFormLoading] = useState(false);
    const user = useSelector(state => state.user.user);
    const [isOpenCVSelector, setIsOpenCVSelector] = useState(false);
    const userToken = useSelector((state) => state.user.token);
    const dispatch = useDispatch();
    const [isProfileCompleteModalOpen, setIsProfileCompleteModalOpen] = useState(false);

    // Check if job is accepting applications
    const isJobAcceptingApplications = job.status === 'opened' || job.status === 'published' || job.status === 'active';
    
    // Get status message based on job status
    const getStatusMessage = () => {
        switch (job.status) {
            case 'paused':
                return t('job_status_messages.paused');
            case 'closed':
                return t('job_status_messages.closed');
            case 'expired':
                return t('job_status_messages.expired');
            default:
                return t('job_status_messages.default');
        }
    };

    useEffect(() => {
        setCvPairs(cvPair);
    }, []);
    const handlePopupClose = (popupName) => {

        setIsApplyPopupOpen(false);
        if (popupName === 'CVAndCoverLetter') {
            setIsOpenCVSelector(false);
        }
        if (popupName === 'SuccessPopup') {
            setIsSuccessPopupOpen(false);
        }
    }

    const handlePopupOpen = (popupName) => {
        if (popupName === 'CVAndCoverLetter') {
            setIsOpenCVSelector(true);
        }
        if (popupName === 'SuccessPopup') {
            setIsSuccessPopupOpen(true);
        }
    }

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append('cv_file', file);

            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}job/uploadCV`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );

                const uploadedFilePath = response.data.path; // Adjust based on your backend response structure
                const newCvPair = {
                    id: Date.now(),
                    isPrimary: cvPairs.length === 0,
                    cv_file: {
                        name: file.name,
                        path: uploadedFilePath,
                    },
                    cv_ref_letter: {
                        name: "No Cover Letter",
                        path: "",
                    },
                    video_cv: null
                };

                setCvPairs([...cvPairs, newCvPair]);
                setSelectedResume(newCvPair.id);
            } catch (error) {
                console.error('File upload failed:', error);
            }
        }
    };

    const handleAddCVAndCoverLetter = (values) => {
        const updatedCVPair = cvPairs.map((item) => ({
            ...item,
            isPrimary: false,
        }));
        const newCVPair = { id: Date.now(), ...values, isPrimary: true };
        setCvPairs([...updatedCVPair, newCVPair])
        setCVPair([...updatedCVPair, newCVPair])
        setSelectedResume(newCVPair.id);
        handlePopupClose('CVAndCoverLetter');
    }

    const checkProfileCompletion = () => {
        if (!user) return false;
        
        const requiredFields = [
            'first_name',
            'last_name',
            'city',
            'country',
            'gender',
            'education',
            'work_experiences'
        ];
        
        const completedFields = requiredFields.filter(field => {
            if (field === 'education') {
                return user.education && user.education.length > 0;
            }
            if (field === 'work_experiences') {
                return user.work_experiences && user.work_experiences.length > 0;
            }
            return user[field] && user[field].trim() !== '';
        });
        return completedFields.length >= requiredFields.length;
    };

    const [isApplyPopupOpen, setIsApplyPopupOpen] = useState(false);
    const navigate = useNavigate();
    const applyJob = async () => {
        if (formLoading) {
            return;
        }

        setFormLoading(true);
        
        toast.promise(
            (async () => {
                const selectCVPair = cvPairs.filter((e) => e.id === selectedResume)
                let cvFilePath;
                let cvRefLetterPath;
                let videoCvPath;
                if (selectCVPair[0].cv_file instanceof File || 
                    (selectCVPair[0].cv_ref_letter && selectCVPair[0].cv_ref_letter instanceof File) || 
                    (selectCVPair[0].video_cv && selectCVPair[0].video_cv instanceof File)) {
                    const formData = new FormData();
                    formData.append('token', userToken);
                    formData.append('cv_file', selectCVPair[0].cv_file);
                    
                    if (selectCVPair[0].cv_ref_letter) {
                        formData.append('cv_ref_letter', selectCVPair[0].cv_ref_letter);
                    }
                    
                    if (selectCVPair[0].video_cv) {
                        formData.append('video_cv', selectCVPair[0].video_cv);
                    }
                    
                    const response = await axios.post(
                        `${process.env.REACT_APP_API_URL}job/uploadCV`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        }
                    );

                    cvFilePath = response.data.cvFilePath;
                    cvRefLetterPath = response.data.cvRefLetterPath;
                    videoCvPath = response.data.videoCvPath;
                }

                const proposalData = {
                    job_id: job.id,
                    token: userToken,
                    status: "New",
                }
                if (selectCVPair.length > 0) {
                    proposalData.proposal_cv = selectCVPair[0].cv_file?.path || cvFilePath;
                    
                    if (selectCVPair[0]?.cv_ref_letter?.path || cvRefLetterPath) {
                        proposalData.proposal_cover_letter = selectCVPair[0]?.cv_ref_letter?.path || cvRefLetterPath;
                    }
                    
                    if (selectCVPair[0]?.video_cv?.path || videoCvPath) {
                        proposalData.proposal_video_cv = selectCVPair[0]?.video_cv?.path || videoCvPath;
                    }
                }
                
                const response = await sendJobProposal(proposalData);
                dispatch(updateApplications([...user.applications, parseInt(job.id, 10)]));
                const target = document.querySelector('.jobDetail');
                if (target) {
                    window.scrollTo({
                        top: target.offsetTop,
                        behavior: 'smooth'
                    });
                }
                setIsApplyPopupOpen(false);
                handlePopupOpen('SuccessPopup');
                return response;
            })(),
            {
                loading: t('general.UI.please_wait'),
                success: <b>{t('general.UI.success')}</b>,
                error: (err) => {
                    return <b>{err.response?.data?.error || t('general.UI.error')}</b>;
                },
            }
        )
        .finally(() => {
            setFormLoading(false);
        });
    }
    const handleApplyClick = (e) => {
        if (!selectedResume) {
            toast.error(t('UploadResume.select_cv_first'));
            return;
        }

        if (user) {
            if (!checkProfileCompletion()) {
                setIsProfileCompleteModalOpen(true);
                return;
            }
            
            if (agreeTermsAndCondition && hasSkillMatch) {
                if (user?.verification_status === 'approved') {
                    applyJob()
                } else {
                    applyJob()
                }
            }
        } else {
            navigate('/login');
        }
    };

    const handleViewDocuments = (cvFile, coverLetter, videoCv) => {
        // Open CV in new tab if it exists
        if (cvFile?.path) {
            window.open(process.env.REACT_APP_API_URL + cvFile.path, '_blank');
        }

        // Open cover letter in new tab if it exists
        if (coverLetter?.path) {
            window.open(process.env.REACT_APP_API_URL + coverLetter.path, '_blank');
        }

        // Open video CV in new tab if it exists
        if (videoCv?.path) {
            window.open(process.env.REACT_APP_API_URL + videoCv.path, '_blank');
        }
    };

    const renderRequiredFieldsList = () => (
        <div className={styles.requiredFieldsList}>
            <p>{t('UploadResume2.required_fields')}:</p>
            <ul>
                <li>{t('personalInformation.name') || "Name"}</li>
                <li>{t('personalInformation.city') || "City"}</li>
                <li>{t('personalInformation.country') || "Country"}</li>
                <li>{t('personalInformation.gender') || "Gender"}</li>
                <li>{t('personalInformation.education') || "Education"}</li>
                <li>{t('personalInformation.work_experience') || "Work Experience"}</li>
            </ul>
        </div>
    );

    return (
        <div className={`${styles.container} formScroll`}>
            <p className={styles.heading}>
                {t('UploadResume.heading')}
            </p>

            {cvPairs.map((item) => (
                <div key={item.id} className={styles.resumeContainer}>
                    <div
                        className={styles.radioContainer}
                        onClick={() => setSelectedResume(item.id)}
                        style={{ cursor: 'pointer' }}
                    >
                        <input
                            type="radio"
                            name="resume"
                            value={item.id}
                            checked={selectedResume === item.id}
                            onChange={() => setSelectedResume(item.id)}
                            className={styles.radioInput}
                        />
                        <label htmlFor={`resume-${item.id}`} className={styles.radioLabel}>
                            <p className={styles.cvTitle}>{item.cv_file.name && (item.cv_file.name.includes('-name:') ? item.cv_file.name.split('-name:').pop() : item.cv_file.name)}</p>
                            <p className={styles.coverLetterTitle}>{item.cv_ref_letter.name && (item.cv_ref_letter.name.includes('-name:') ? item.cv_ref_letter.name.split('-name:').pop() : item.cv_ref_letter.name)}</p>
                            {item.video_cv && <p className={styles.coverLetterTitle}>{item.video_cv.name && (item.video_cv.name.includes('-name:') ? item.video_cv.name.split('-name:').pop() : item.video_cv.name)}</p>}
                        </label>
                    </div>
                    <img
                        src={EyeBlueIcon}
                        alt={t('talentsHeader.sortBy')}
                        className={styles.sortIcon}
                        onClick={() => handleViewDocuments(item.cv_file, item.cv_ref_letter, item.video_cv)}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
            ))}
        {user?.verification_status=='approved' &&
            <div className={styles.resumeContainer}>
                <div
                    className={styles.radioContainer}>
                    <p className={styles.cvTitle}>{t('UploadResume.upload_cv')}</p>
                </div>
                <label
                >
                    <img
                        onClick={() => { handlePopupOpen("CVAndCoverLetter") }}
                        src={PlusIcon}
                        alt={t('talentsHeader.sortBy')}
                        className={styles.sortIcon}
                        style={{ cursor: "pointer" }}
                    />
                </label>
                <input
                    type="file"
                    id="cv-upload"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                />
            </div>
            }
            <div className={styles.checkboxWrapper} style={{ justifyContent: "flex-start", width: "100%" }}>
                <input
                    type="checkbox"
                    cover
                    id="exclusive-jobs"
                    checked={agreeTermsAndCondition}
                    onChange={() => setAgreeTermsAndCondition(!agreeTermsAndCondition)}
                    className={styles.checkbox}
                />
                <label htmlFor="exclusive-jobs" className={styles.checkboxLabel}>
                    {t('UploadResume.agree_terms')}
                </label>
            </div>
            <div>
                {!isJobAcceptingApplications ? (
                    <div className={styles.statusMessage}>
                        <p>{getStatusMessage()}</p>
                    </div>
                ) : (user?.account_type !== 'employer' && !user?.applications?.includes(parseInt(job.id, 10))) && (
                    <>
                        {(job.exlusiveJob && user.verification_status !== 'approved') ?
                            <BubbleButton
                                onClick={() => { navigate('/subscriptions'); }}
                                dark
                                headerDarkButton
                                type="default"
                                small
                            >
                                {t('forms.verification.get_verified_to_apply')}
                            </BubbleButton>
                            :
                            <BubbleButton
                                onClick={handleApplyClick}
                                dark
                                style={{
                                    backgroundColor: agreeTermsAndCondition && hasSkillMatch ? '#0464B2' : 'lightGray',
                                    border: agreeTermsAndCondition && hasSkillMatch ? "1px solid #0464B2" : "0px solid #0464B2"
                                }}
                                headerDarkButton
                                type="default"
                                small
                            >
                                {t('UploadResume.apply_button')}
                            </BubbleButton>
                        }
                    </>
                )}
                {user?.applications?.includes(parseInt(job.id, 10)) && <strong style={{ color: '#34A853' }}>{t('general.UI.applied')}</strong>}

            </div>
            {isOpenCVSelector &&
                <BasicPopup
                    isOpen={isOpenCVSelector}
                    closePopup={() => handlePopupClose('CVAndCoverLetter')}
                >
                    <CVAndCoverLetter user={user} onSubmit={handleAddCVAndCoverLetter} />
                </BasicPopup>
            }
            
            <GlobalModal
                show={isProfileCompleteModalOpen}
                onClose={() => setIsProfileCompleteModalOpen(false)}
                title={t('UploadResume2.incomplete_profile')}
                message={t('UploadResume2.complete_profile_message')}
                children={renderRequiredFieldsList()}
                primaryButtonText={t('UploadResume2.complete_profile')}
                secondaryButtonText={t('general.UI.cancel')}
                navigateTo="/profile"
                secondaryButtonAction={() => setIsProfileCompleteModalOpen(false)}
            />
        </div>
    );
}

export default UploadResume;
